import React, { useState, useEffect } from "react";
import { WebLink, SiteWide, Images } from "./customComponents";
import Section from "./layouts/Section";
import SecurityCenterModal from "./SecurityCenterModal";
import { useSelector } from "react-redux";
import { homeScreenStatSelect, userSelect, allRemoteSettingsSelect } from "../redux/selectors/index";
import { useNavigate } from "react-router-dom";
import { money } from "../utility/Helper1";
import APIRequests from "../utility/APIRequests";

// Material-UI Icons
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DiamondIcon from "@mui/icons-material/Diamond";
import BoltIcon from "@mui/icons-material/Bolt";
import StarIcon from "@mui/icons-material/Star";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

// Balance card quick-action icons (chat-app themed)
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import ShareIcon from "@mui/icons-material/Share";
import PlusIcon from "@mui/icons-material/Add";
import PublicIcon from "@mui/icons-material/Public";
import {
    RiFacebookFill,
    RiWhatsappFill,
    RiTelegramFill,
    RiTwitterXFill,
    RiInstagramFill,
    RiYoutubeFill,
    RiLinkedinFill,
    RiTiktokFill
} from "react-icons/ri";

// Service Icons
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GroupIcon from "@mui/icons-material/Group";
import SecurityIcon from "@mui/icons-material/Security";
import AccessibilityNewTwoToneIcon from "@mui/icons-material/AccessibilityNewTwoTone";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

// ── Sparkline mini-chart component ──────────────────────────────────────────
function Sparkline({ data, color, fill }) {
    const w = 80, h = 32;
    const min = Math.min(...data), max = Math.max(...data);
    const pts = data.map((v, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = h - ((v - min) / (max - min || 1)) * (h - 4) - 2;
        return `${x},${y}`;
    }).join(" ");
    const areaClose = `${w},${h} 0,${h}`;
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block" }}>
            {fill && <polygon points={`0,${h} ${pts} ${areaClose}`} fill={fill} opacity="0.12" />}
            <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// ── Radial progress ring component ──────────────────────────────────────────
function Ring({ pct, size = 56, stroke = 4, color = "#3b82f6", bg = "rgba(59, 130, 246, 0.08)" }) {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;
    return (
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={bg} strokeWidth={stroke} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
        </svg>
    );
}

// ── Activity bar chart component (replaced with premium GitHub Activity grid) ──
function GithubActivity() {
    const [activity, setActivity] = useState({
        Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
    });
    const [loading, setLoading] = useState(true);
    const [hoveredDay, setHoveredDay] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchActivity = async () => {
            try {
                const api = new APIRequests();
                const res = await api.runGet("chatty/weekly-activity");
                if (res && res.code === 1 && res.data && res.data.activity) {
                    if (isMounted) {
                        setActivity(res.data.activity);
                    }
                }
            } catch (err) {
                console.error("Error fetching weekly activity:", err);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        fetchActivity();
        return () => { isMounted = false; };
    }, []);

    // Get color based on count (GitHub style)
    const getColor = (count) => {
        if (count === 0) return "var(--border-color)"; // soft slate/gray/border color in premium theme
        if (count <= 2) return "#9be9a8";
        if (count <= 4) return "#40c463";
        if (count <= 6) return "#30a14e";
        return "#216e39";
    };

    const days = [
        { label: "Mon", key: "Mon" },
        { label: "Tue", key: "Tue" },
        { label: "Wed", key: "Wed" },
        { label: "Thu", key: "Thu" },
        { label: "Fri", key: "Fri" },
        { label: "Sat", key: "Sat" },
        { label: "Sun", key: "Sun" }
    ];

    const totalMessages = Object.values(activity).reduce((sum, val) => sum + val, 0);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
            <style>{`
                @keyframes tooltipFadeIn {
                    from { opacity: 0; transform: translate(-50%, 4px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
            `}</style>

            {/* Header info */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "'DM Sans', sans-serif" }}>
                    Total sent: <strong style={{ color: "#2563eb", fontWeight: 700 }}>{totalMessages} messages</strong>
                </span>

                {/* Micro legend */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, color: "var(--text-muted)", fontFamily: "'DM Sans', sans-serif" }}>Less</span>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: "var(--border-color)" }} />
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: "#9be9a8" }} />
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: "#40c463" }} />
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: "#30a14e" }} />
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: "#216e39" }} />
                    <span style={{ fontSize: 9, color: "var(--text-muted)", fontFamily: "'DM Sans', sans-serif" }}>More</span>
                </div>
            </div>

            {/* Grid display */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, width: "100%" }}>
                {days.map((d) => {
                    const count = activity[d.key] || 0;
                    const isHovered = hoveredDay === d.key;
                    return (
                        <div
                            key={d.key}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 6,
                                flex: 1,
                                position: "relative"
                            }}
                            onMouseEnter={() => setHoveredDay(d.key)}
                            onMouseLeave={() => setHoveredDay(null)}
                        >
                            {/* Hover tooltip */}
                            {isHovered && (
                                <div style={{
                                    position: "absolute",
                                    bottom: "100%",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    marginBottom: 8,
                                    background: "#0f172a",
                                    color: "#ffffff",
                                    fontSize: 10,
                                    padding: "4px 8px",
                                    borderRadius: 6,
                                    whiteSpace: "nowrap",
                                    boxShadow: "0 4px 12px rgba(15,23,42,0.15)",
                                    zIndex: 10,
                                    fontFamily: "'DM Sans', sans-serif",
                                    animation: "tooltipFadeIn 0.15s ease-out forwards"
                                }}>
                                    {count} {count === 1 ? "message" : "messages"}
                                    <div style={{
                                        position: "absolute",
                                        top: "100%",
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        width: 0,
                                        height: 0,
                                        borderLeft: "5px solid transparent",
                                        borderRight: "5px solid transparent",
                                        borderTop: "5px solid #0f172a"
                                    }} />
                                </div>
                            )}

                            {/* Contribution square */}
                            <div
                                style={{
                                    width: "100%",
                                    aspectRatio: "1/1",
                                    borderRadius: 8,
                                    background: getColor(count),
                                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                    transform: isHovered ? "scale(1.12)" : "scale(1)",
                                    boxShadow: isHovered && count > 0 ? "0 4px 12px rgba(34, 197, 94, 0.3)" : "none",
                                    cursor: "pointer"
                                }}
                            />

                            {/* Label */}
                            <span style={{
                                fontSize: 10,
                                fontWeight: isHovered ? 700 : 500,
                                color: isHovered ? "#2563eb" : "#64748b",
                                fontFamily: "'DM Sans', sans-serif",
                                transition: "color 0.2s ease"
                            }}>
                                {d.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Social Platforms config ───────────────────────────────────────────────────
const SOCIAL_PLATFORMS = [
    { name: 'Facebook', key: 'facebook', icon: RiFacebookFill, color: '#1877F2' },
    { name: 'WhatsApp', key: 'whatsapp', icon: RiWhatsappFill, color: '#25D366' },
    { name: 'Telegram', key: 'telegram', icon: RiTelegramFill, color: '#0088CC' },
    { name: 'Twitter', key: 'twitter', icon: RiTwitterXFill, color: '#94a3b8' },
    { name: 'Instagram', key: 'instagram', icon: RiInstagramFill, color: '#E1306C' },
    { name: 'YouTube', key: 'youtube', icon: RiYoutubeFill, color: '#FF0000' },
    { name: 'LinkedIn', key: 'linkedin', icon: RiLinkedinFill, color: '#0A66C2' },
    { name: 'TikTok', key: 'tiktok', icon: RiTiktokFill, color: '#69C9D0' },
];

// ── Social Popover (fixed-position, escapes overflow:hidden) ──────────────────
function SocialPopover({ siteSettings }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = React.useRef(null);

    // close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (btnRef.current && !btnRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const activeSocials = SOCIAL_PLATFORMS.filter(p => {
        const url = siteSettings?.site_settings?.[p.key];
        return url && url.trim() !== '';
    });

    const handleLink = (url) => {
        let href = url.trim();
        if (!/^https?:\/\//i.test(href)) href = 'https://' + href;
        window.open(href, '_blank', 'noopener,noreferrer');
        setOpen(false);
    };

    const toggle = () => {
        if (btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 10, left: r.left + r.width / 2 });
        }
        setOpen(v => !v);
    };

    return (
        <>
            <style>{`
                @keyframes spIn {
                    from { opacity: 0; transform: translateX(-50%) translateY(-6px) scale(0.96); }
                    to   { opacity: 1; transform: translateX(-50%) translateY(0)    scale(1);    }
                }
                .sp-row { transition: background 0.13s; }
                .sp-row:hover { background: var(--hover-bg, rgba(100,116,139,0.1)) !important; }
                .sp-icon { transition: transform 0.15s ease; display: flex; align-items: center; }
                .sp-row:hover .sp-icon { transform: scale(1.15); }
            `}</style>

            {/* Trigger button */}
            <button
                ref={btnRef}
                onClick={toggle}
                style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    background: 'transparent', border: 'none', cursor: 'pointer', outline: 'none', padding: 0,
                }}
            >
                <span style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 46, height: 46, borderRadius: '50%',
                    background: open ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)',
                    border: '1.5px solid rgba(255,255,255,0.35)',
                    backdropFilter: 'blur(6px)',
                    transition: 'background 0.15s',
                }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
                    onMouseLeave={e => e.currentTarget.style.background = open ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)'}
                >
                    <PublicIcon style={{ color: '#ffffff', fontSize: 22 }} />
                </span>
                <span style={{
                    color: 'rgba(255,255,255,0.82)', fontSize: 10,
                    fontFamily: "'DM Sans',sans-serif", fontWeight: 500, letterSpacing: '0.02em',
                }}>Social</span>
            </button>

            {/* Fixed popover — not clipped by overflow:hidden */}
            {open && (
                <div
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.left,
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        minWidth: 190,
                        background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: 14,
                        boxShadow: '0 10px 40px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.1)',
                        padding: '6px 0 8px',
                        animation: 'spIn 0.2s cubic-bezier(0.34,1.4,0.64,1) both',
                    }}
                >
                    {/* Arrow caret pointing up toward trigger */}
                    <div style={{
                        position: 'absolute', top: -6, left: '50%',
                        width: 11, height: 11,
                        background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRight: 'none', borderBottom: 'none',
                        transform: 'translateX(-50%) rotate(45deg)',
                        pointerEvents: 'none',
                    }} />

                    {/* Section label */}
                    <p style={{
                        margin: 0, padding: '6px 14px 4px',
                        fontSize: 10, fontWeight: 700,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        color: 'var(--text-muted)',
                        fontFamily: "'DM Sans',sans-serif",
                    }}></p>
                    <div style={{ height: 1, background: 'var(--card-border)', marginBottom: 4 }} />

                    {activeSocials.length === 0 ? (
                        <p style={{
                            margin: 0, padding: '10px 14px',
                            fontSize: 12, color: 'var(--text-muted)',
                            fontFamily: "'DM Sans',sans-serif",
                        }}>No social links configured.</p>
                    ) : (
                        activeSocials.map(p => {
                            const PlatformIcon = p.icon;
                            const url = siteSettings?.site_settings?.[p.key];
                            return (
                                <button
                                    key={p.key}
                                    className="sp-row"
                                    onClick={() => handleLink(url)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 10,
                                        width: '100%', padding: '8px 14px',
                                        background: 'transparent', border: 'none',
                                        cursor: 'pointer', textAlign: 'left',
                                    }}
                                >
                                    <span className="sp-icon" style={{ color: p.color, fontSize: 18, lineHeight: 1 }}>
                                        <PlatformIcon />
                                    </span>
                                    <span style={{
                                        fontSize: 13, fontWeight: 500,
                                        color: 'var(--text-main)',
                                        fontFamily: "'DM Sans',sans-serif",
                                    }}>{p.name}</span>
                                </button>
                            );
                        })
                    )}
                </div>
            )}
        </>
    );
}

function Home() {
    const [selectedArrayIndex, setSelectedArrayIndex] = useState(0);
    const [securityModalOpen, setSecurityModalOpen] = useState(false);

    // Dynamic Goals Progress state variables
    const [chatsActivity, setChatsActivity] = useState(0);
    const [earningsCap, setEarningsCap] = useState(0);
    const [referralsGoal, setReferralsGoal] = useState(0);
    const [loadingStats, setLoadingStats] = useState(true);
    const [liveActivities, setLiveActivities] = useState([]);


    const finance = useSelector(homeScreenStatSelect);
    const userData = useSelector(userSelect);
    const siteSettings = useSelector(allRemoteSettingsSelect);
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;
        const fetchStats = async () => {
            try {
                const api = new APIRequests();

                // 1. Fetch Chat Sessions
                const chatRes = await api.runGet("chatty/load");
                if (isMounted && chatRes && chatRes.code === 1 && chatRes.data) {
                    const chats = chatRes.data.chats || [];
                    const activeChats = chats.filter(c => Number(c.active) === 1);

                    // Chats Activity: Average progress of active chats.
                    // Fall back to all chats if no active chats are found.
                    const chatsToUse = activeChats.length > 0 ? activeChats : chats;
                    if (chatsToUse.length > 0) {
                        const totalProgress = chatsToUse.reduce((sum, c) => sum + Number(c.progress || 0), 0);
                        setChatsActivity(Math.round(totalProgress / chatsToUse.length));
                    } else {
                        setChatsActivity(0);
                    }

                    // Earnings Cap: Ratio of accumulated profit to potential maximum profit across sessions.
                    if (chatsToUse.length > 0) {
                        let totalProfit = 0;
                        let totalMaxProfit = 0;
                        chatsToUse.forEach(c => {
                            const maxProfit = Number(c.capital || 0) * (Number(c.dailyRoi || 0) / 100) * Number(c.tenure || 0);
                            totalProfit += Number(c.profit || 0);
                            totalMaxProfit += maxProfit;
                        });
                        if (totalMaxProfit > 0) {
                            setEarningsCap(Math.min(100, Math.round((totalProfit / totalMaxProfit) * 100)));
                        } else {
                            setEarningsCap(0);
                        }
                    } else {
                        setEarningsCap(0);
                    }
                }

                // 2. Fetch Referral Downlines
                if (userData && userData.myrefid) {
                    const refRes = await api.runGet(`user/referral/${userData.myrefid}`);
                    if (isMounted && refRes && refRes.code === 1 && refRes.data) {
                        const level1 = refRes.data.referrals?.level1 || [];
                        const refCount = level1.length;
                        const goalTarget = 5; // Direct referral goal target
                        setReferralsGoal(Math.min(100, Math.round((refCount / goalTarget) * 100)));
                    }
                }

                // 3. Fetch Live Activities / Notifications
                const notifRes = await api.runGet("notifications/load?limit=4");
                if (isMounted && notifRes && notifRes.code === 1 && notifRes.data) {
                    setLiveActivities(notifRes.data.list || []);
                }

            } catch (err) {
                console.error("Error loading home stats:", err);
            } finally {
                if (isMounted) {
                    setLoadingStats(false);
                }
            }
        };

        if (userData?.myrefid) {
            fetchStats();
        }
    }, [userData]);

    // Portfolio calculations using dynamic Redux store values
    const currentWallet = finance?.[selectedArrayIndex];
    const totalPortfolioBalance = (Number(currentWallet?.balance || 0) + Number(currentWallet?.commissionRO || 0));

    // Decimals splitting for clean unit sizes
    const formattedMoney = money(totalPortfolioBalance, currentWallet?.type ?? (selectedArrayIndex === 0 ? 22 : 141));
    const dotIndex = formattedMoney.lastIndexOf('.');
    let mainBalancePart = formattedMoney;
    let centsBalancePart = "";
    if (dotIndex !== -1) {
        mainBalancePart = formattedMoney.substring(0, dotIndex);
        centsBalancePart = formattedMoney.substring(dotIndex);
    }

    const activeWallet = finance?.[0];
    const isNaira = String(activeWallet?.type) === '141';

    // Chat-app quick actions — icon-only chips on the balance card
    const cardActions = [
        { Icon: ChatBubbleOutlineIcon, label: "Messages", action: () => navigate("/chat") },
        { Icon: GroupsIcon, label: "Community", action: () => navigate("/affiliate-center") },
        isNaira
            ? { Icon: AccountBalanceIcon, label: "Bank", action: () => navigate("/bank") }
            : { Icon: PlusIcon, label: "Wallet", action: () => navigate("/wallet") },
        { Icon: ShareIcon, label: "Invite", action: () => navigate("/affiliate-center") },
    ];

    // Dynamic stats pulling live figures from Redux selector
    const stats = [
        {
            label: `${finance?.[0]?.name || 'Wallet'} Balance`,
            value: `${money(finance?.[0]?.balance ?? '0.00', finance?.[0]?.type ?? 22)}`,
            delta: '+3.4%',
            up: true,
            spark: [30, 45, 35, 60, 55, 80, 75, 95],
            color: '#3b82f6'
        },
        {
            label: `${finance?.[0]?.name || 'Wallet'} Interest`,
            value: `${money(finance?.[0]?.commissionRO ?? '0.00', finance?.[0]?.type ?? 22)}`,
            delta: '+12.4%',
            up: true,
            spark: [20, 30, 25, 40, 38, 50, 45, 60],
            color: '#22c55e'
        },
        ...(finance?.[1] ? [
            {
                label: `${finance?.[1]?.name || 'Wallet'} Balance`,
                value: `${money(finance?.[1]?.balance ?? '0.00', finance?.[1]?.type ?? 141)}`,
                delta: '-1.2%',
                up: false,
                spark: [60, 55, 50, 45, 40, 38, 35, 30],
                color: '#f59e0b'
            },
            {
                label: `${finance?.[1]?.name || 'Wallet'} Interest`,
                value: `${money(finance?.[1]?.commissionRO ?? '0.00', finance?.[1]?.type ?? 141)}`,
                delta: 'Personal best',
                up: true,
                spark: [5, 8, 7, 10, 9, 12, 11, 14],
                color: '#a855f7'
            }
        ] : [])
    ];

    // Real investment services mapping (unused in rendering but kept for reference)
    const services = [
        { icon: ShowChartIcon, name: 'Bank Vault', desc: 'Automated trading', to: '/bank' },
        { icon: AccountBalanceWalletIcon, name: 'Digital Wallet', desc: 'Secure storage', to: '/wallet' },
        { icon: GroupIcon, name: 'Team Network', desc: 'Referral network', to: '/affiliate-center' },
        { icon: SecurityIcon, name: 'Security Center', desc: 'Account protection', to: null, action: 'modal' },
        { icon: AccessibilityNewTwoToneIcon, name: 'Invite & Earn', desc: 'Referral program', to: '/affiliate-center' },
        { icon: SettingsIcon, name: 'Profile Settings', desc: 'Account management', to: '/personal-information' },
    ];

    const getNotificationStyle = (msg = "") => {
        const lower = msg.toLowerCase();
        if (lower.includes("referral") || lower.includes("joined")) {
            return {
                icon: <BoltIcon style={{ fontSize: 16 }} />,
                color: "#3b82f6",
                title: "Referral Network"
            };
        } else if (lower.includes("reward") || lower.includes("profit") || lower.includes("earned") || lower.includes("capital")) {
            return {
                icon: <MonetizationOnIcon style={{ fontSize: 16 }} />,
                color: "#22c55e",
                title: "Earning Credited"
            };
        } else if (lower.includes("withdrawal") || lower.includes("paid") || lower.includes("confirmed")) {
            return {
                icon: <ArrowDownwardIcon style={{ fontSize: 16 }} />,
                color: "#a855f7",
                title: "Withdrawal Success"
            };
        } else if (lower.includes("completed")) {
            return {
                icon: <StarIcon style={{ fontSize: 16 }} />,
                color: "#f59e0b",
                title: "Session Completed"
            };
        } else {
            return {
                icon: <NotificationsIcon style={{ fontSize: 16 }} />,
                color: "#64748b",
                title: "Notification"
            };
        }
    };

    const formatRelativeTime = (timestamp) => {
        if (!timestamp) return "";
        const now = Math.floor(Date.now() / 1000);
        const diffSec = now - Number(timestamp);
        const diffMin = Math.floor(diffSec / 60);
        const diffHr = Math.floor(diffMin / 60);
        const diffDays = Math.floor(diffHr / 24);

        if (diffSec < 60) return "Just now";
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffHr < 24) return `${diffHr}h ago`;
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) return `${diffDays}d ago`;

        const date = new Date(Number(timestamp) * 1000);
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    };


    return (
        <SiteWide title={"Home"}>
            <Section hideHeader={true} overlayText={""} loading={false} title={'Home'}>
                <div className="flex flex-col gap-4 pb-6">

                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p style={{ color: "var(--text-muted)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>Welcome back 👋</p>
                            <h2 style={{ color: "var(--text-main)", fontSize: 20, fontWeight: 700, fontFamily: "'Syne',sans-serif", lineHeight: 1.2, margin: 0, marginTop: 2 }}>
                                Hello, <span style={{ color: "#2563eb" }}>{userData?.username || "Investor"}</span>
                            </h2>
                        </div>
                    </div>

                    {/* Premium Glass Balance Card */}
                    <div className="rounded-2xl p-5 relative overflow-hidden" style={{ background: "linear-gradient(135deg,#1e3a8a 0%,#2563eb 50%,#3b82f6 100%)", border: "1px solid rgba(96,165,250,0.15)", boxShadow: "0 10px 25px -5px rgba(37,99,235,0.3)" }}>
                        <div className="absolute top-0 right-0 opacity-10" style={{ width: 160, height: 160, background: "radial-gradient(circle,#93c5fd,transparent)", borderRadius: "50%", transform: "translate(40px,-40px)" }} />
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'DM Sans',sans-serif" }}>
                                        {currentWallet?.name || "Portfolio"} Profile
                                    </span>
                                </div>
                                <p style={{ color: "#fff", fontSize: 32, fontWeight: 800, fontFamily: "'Syne',sans-serif", lineHeight: 1.1, margin: 0, marginTop: 8 }}>
                                    {mainBalancePart}
                                    <span style={{ fontSize: 16, fontWeight: 400 }}>{centsBalancePart}</span>
                                </p>
                                <div className="flex items-center gap-1 mt-2">
                                    {(currentWallet?.weeklyChange ?? 8.3) >= 0 ? (
                                        <>
                                            <ArrowUpwardIcon style={{ fontSize: 13, color: "#86efac" }} />
                                            <span style={{ color: "#86efac", fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>
                                                +{currentWallet?.weeklyChange ?? "8.3"}% this week
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <ArrowDownwardIcon style={{ fontSize: 13, color: "#f87171" }} />
                                            <span style={{ color: "#f87171", fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>
                                                {currentWallet?.weeklyChange}% this week
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="rounded-xl flex items-center justify-center" style={{ width: 48, height: 48, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)" }}>
                                <DiamondIcon style={{ color: "#ffffff", fontSize: 24 }} />
                            </div>
                        </div>
                        {/* Quick action icon-chip row */}
                        <div style={{ display: "flex", gap: 12, marginTop: 20, justifyContent: "space-around" }}>
                            {cardActions.map(({ Icon, label, action }) => (
                                <button
                                    key={label}
                                    onClick={action}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 6,
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        outline: "none",
                                        padding: 0,
                                    }}
                                >
                                    <span style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: 46,
                                        height: 46,
                                        borderRadius: "50%",
                                        background: "rgba(255,255,255,0.18)",
                                        border: "1.5px solid rgba(255,255,255,0.35)",
                                        backdropFilter: "blur(6px)",
                                        transition: "transform 0.15s, background 0.15s",
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.28)"}
                                        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.18)"}
                                    >
                                        <Icon style={{ color: "#ffffff", fontSize: 22 }} />
                                    </span>
                                    <span style={{
                                        color: "rgba(255,255,255,0.82)",
                                        fontSize: 10,
                                        fontFamily: "'DM Sans',sans-serif",
                                        fontWeight: 500,
                                        letterSpacing: "0.02em",
                                    }}>{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Weekly Activity chart */}
                    <div className="rounded-2xl p-4" style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", boxShadow: "var(--card-shadow)" }}>
                        <div className="flex items-center justify-between mb-3">
                            <p style={{ color: "var(--text-main)", fontSize: 13, fontWeight: 600, fontFamily: "'Syne',sans-serif", margin: 0 }}>Weekly Activity</p>
                            <span className="text-xs px-2 py-1 rounded-lg" style={{ background: "rgba(59,130,246,0.08)", color: "#2563eb", fontFamily: "'DM Sans',sans-serif" }}>This week</span>
                        </div>
                        <GithubActivity />
                    </div>

                    {/* Goals Progress */}
                    <div className="rounded-2xl p-4" style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", boxShadow: "var(--card-shadow)" }}>
                        <p style={{ color: "var(--text-main)", fontSize: 13, fontWeight: 600, fontFamily: "'Syne',sans-serif", marginBottom: 12, margin: 0 }}>Goals Progress</p>
                        <div className="grid grid-cols-3 gap-2 mt-3">
                            {[
                                { label: "Chats Activity", pct: chatsActivity, color: "#3b82f6" },
                                { label: "Earnings Cap", pct: earningsCap, color: "#a855f7" },
                                { label: "Referrals Goal", pct: referralsGoal, color: "#22c55e" },
                            ].map((g) => (
                                <div key={g.label} className="flex flex-col items-center gap-2">
                                    <div className="relative">
                                        <Ring pct={g.pct} color={g.color} />
                                        <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "var(--text-main)", fontSize: 11, fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>{g.pct}%</span>
                                    </div>
                                    <span style={{ color: "var(--text-muted)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", textAlign: "center" }}>{g.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Recent activity */}
                    <div className="rounded-2xl p-4" style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", boxShadow: "var(--card-shadow)" }}>
                        <div className="flex items-center justify-between mb-3">
                            <p style={{ color: "var(--text-main)", fontSize: 13, fontWeight: 600, fontFamily: "'Syne',sans-serif", margin: 0 }}>Recent Activity</p>
                            <span
                                onClick={() => navigate('/notification')}
                                style={{ fontSize: 11, fontWeight: 600, color: "#2563eb", fontFamily: "'DM Sans',sans-serif", cursor: "pointer", transition: "color 0.15s" }}
                                onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                            >
                                See More
                            </span>
                        </div>
                        <div className="flex flex-col gap-3 mt-3">
                            {liveActivities.length === 0 ? (
                                <div className="text-center py-6">
                                    <p style={{ color: "var(--text-muted)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>No recent activity yet.</p>
                                </div>
                            ) : (
                                liveActivities.map((a, i) => {
                                    const style = getNotificationStyle(a.message);
                                    return (
                                        <div key={i} className="flex items-center gap-3">
                                            <div className="rounded-xl flex items-center justify-center shrink-0" style={{ width: 36, height: 36, background: `${style.color}0a`, border: `1px solid ${style.color}1c`, color: style.color }}>
                                                {style.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p style={{ color: "var(--text-main)", fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", margin: 0, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }} title={a.message}>{a.message}</p>
                                                <p style={{ color: "var(--text-muted)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", margin: 0, marginTop: 2 }}>{style.title}</p>
                                            </div>
                                            <span style={{ color: "var(--text-muted)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap" }}>{formatRelativeTime(a.timestamp)}</span>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>

                    {/* Social Media Strip */}
                    {(() => {
                        const activeSocials = SOCIAL_PLATFORMS.filter(p => {
                            const url = siteSettings?.site_settings?.[p.key];
                            return url && url.trim() !== '';
                        });
                        if (activeSocials.length === 0) return null;
                        return (
                            <div style={{ paddingBottom: 8 }}>
                                <p style={{
                                    color: 'var(--text-muted)', fontSize: 11, fontWeight: 600,
                                    textTransform: 'uppercase', letterSpacing: '0.07em',
                                    fontFamily: "'DM Sans',sans-serif",
                                    margin: '0 0 12px',
                                }}>Follow Us</p>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    {activeSocials.map(p => {
                                        const PlatformIcon = p.icon;
                                        const url = siteSettings?.site_settings?.[p.key];
                                        const href = /^https?:\/\//i.test(url.trim()) ? url.trim() : 'https://' + url.trim();
                                        return (
                                            <a
                                                key={p.key}
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={p.name}
                                                style={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 40, height: 40, borderRadius: 12,
                                                    background: 'var(--card-bg)',
                                                    border: '1px solid var(--card-border)',
                                                    color: p.color,
                                                    fontSize: 19,
                                                    textDecoration: 'none',
                                                    transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = `0 6px 18px ${p.color}33`;
                                                    e.currentTarget.style.borderColor = `${p.color}66`;
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)';
                                                    e.currentTarget.style.borderColor = 'var(--card-border)';
                                                }}
                                            >
                                                <PlatformIcon />
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}

                </div>
            </Section>

            {/* Security Center Modal */}
            <SecurityCenterModal
                open={securityModalOpen}
                onClose={() => setSecurityModalOpen(false)}
            />


        </SiteWide>
    );
}

export default Home;